import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface Applicant {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: string;
  numberOfDependents?: number;
  maritalStatus?: string;
  address?: Address;
  mailingAddress?: {
    housingStatus?: string;
  };
  employment?: Employment;
  incomes?: Income[];
  expenses?: Expenses[];
  liabilities?: Liabilities;
  id?: string;
}
interface Liabilities {
  creditCardLimits?: number;
  sumOfAllOtherLoans?: number;
  totalMonthlyRepaymentsOnOtherLoans?: number;
  id?: string;
}
interface Income {
  type: string;
  amount: number;
  frequency?: string;
  id?: string;
}
interface Expenses {
  id?: string;
  type: string;
  amount: number;
}
interface Employment {
  employerName?: string;
  employmentType?: string;
  income?: number;
  frequency?: string;
  id?: string;
}
interface Address {
  street: string;
  state: string;
  postCode: string;
}

interface LoanApplication {
  applicationType?: string;
  loanType?: string;
  loanFor?: string;
  rateType?: string;
  loanAmount?: number;
  loanTerm?: number;
  refinanceAmount?: number;
  balloonPayment?: string | number;
  coApplicantRelationship?: string;
  coApplicantsSameAddress?: boolean;
  interestRate?: number;
  assessmentRate?: number;
  id?: string;
}
interface VehicleDetail {
  make?: string;
  model?: string;
  year?: number;
  purchaseFrom?: string;
  vehicleCondition?: string;
  classId?: string;
  makeId?: string;
  brand?: string;
  price?: number;
  vinNumber?: string | number;
  licensePlateNumber?: string | number;
  manuallyModel?: boolean;
  isCarManuallyModel?: boolean;
  id?: string;
}

interface LoanData {
  vehicleDetail?: VehicleDetail;
  loanApplication?: LoanApplication;
  applicants?: Applicant;
}
interface SavedIds {
  applicantID?: string;
  vehicleDetailID?: string;
  loanApplicationID?: string;
  employmentID?: string;
  incomeIDs?: string[];
  secondIncomeIDs?: string[];
  liabilitieID?: string;
  secondliabilitieID?: string;
  secondEmploymentID?: string;
  secondApplicantID?: string;
  bankDocLink?: string;
  secondBankDocLink?: string;
  applicationReferenceNumber?: string;
  applicationStatus?: string;
}
interface Serviceability {
  maxBorrowingAmount?: number | string;
}
interface JointApplicationState {
  [category: string]: Record<string, any>;
}
interface StepState {
  currentStep: number;
  stepsData: LoanData;
  savedIds: SavedIds;
  serviceability: Serviceability;
  jointApplication: JointApplicationState; // Move jointApplication outside stepsData
}

const initialState: StepState = {
  currentStep: 0,
  stepsData: {
    vehicleDetail: {
      manuallyModel: false,
    },
  },
  savedIds: {},
  serviceability: {},
  jointApplication: {},
};
export const loanTypesSteps = createSlice({
  name: "loanTypesSteps",
  initialState,
  reducers: {
    nextStep: (state) => {
      if (state.currentStep) state.currentStep += 1;
    },
    prevStep: (state) => {
      if (state.currentStep >= 1) state.currentStep -= 1;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    addStepsData: (state, action: PayloadAction<LoanData>) => {
      state.stepsData = { ...state.stepsData, ...action.payload };
    },
    setSavedIds: (state, action: PayloadAction<SavedIds>) => {
      state.savedIds = { ...state.savedIds, ...action.payload };
    },
    setServiceability: (state, action: PayloadAction<Serviceability>) => {
      state.serviceability = { ...state.serviceability, ...action.payload };
    },
    addJointApplicationData: (
      state,
      action: PayloadAction<{ category: string; data: Record<string, any> }>
    ) => {
      const { category, data } = action.payload;
      state.jointApplication[category] = data;
    },
    resetJointApplicationData: (state) => {
      state.jointApplication = {};
    },
    resetStepsData: (state) => {
      state.stepsData.applicants = {};
    },
  },
});

export const {
  nextStep,
  prevStep,
  setStep,
  addStepsData,
  resetStepsData,
  setSavedIds,
  setServiceability,
  addJointApplicationData,
  resetJointApplicationData,
} = loanTypesSteps.actions;

export default loanTypesSteps.reducer;
